/**
 * TypeScript types for Automation Rules
 */

// Google Calendar Color IDs (0 = default/no color, 1-11 = Google's color palette)
export type GoogleCalendarColorId = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | '11'

// Google Calendar Color Names
export const GOOGLE_CALENDAR_COLORS = {
  '0': { name: 'Default', hex: '#808080' },
  '1': { name: 'Lavender', hex: '#7986cb' },
  '2': { name: 'Sage', hex: '#33b679' },
  '3': { name: 'Grape', hex: '#8e24aa' },
  '4': { name: 'Flamingo', hex: '#e67c73' },
  '5': { name: 'Banana', hex: '#f6bf26' },
  '6': { name: 'Tangerine', hex: '#f4511e' },
  '7': { name: 'Peacock', hex: '#039be5' },
  '8': { name: 'Graphite', hex: '#616161' },
  '9': { name: 'Blueberry', hex: '#3f51b5' },
  '10': { name: 'Basil', hex: '#0b8043' },
  '11': { name: 'Tomato', hex: '#d50000' },
} as const

// Condition operators
export type ConditionOperator = 
  | 'equals'
  | 'contains'
  | 'contains_any'
  | 'contains_all'
  | 'greater_than'
  | 'less_than'
  | 'starts_after'
  | 'ends_before'
  | 'gte'
  | 'lte'
  | 'exists'

// Condition fields
export type ConditionField = 
  | 'title'
  | 'attendees'
  | 'attendees_domain'
  | 'attendees_count'
  | 'location'
  | 'scheduled_time'
  | 'scheduled_duration'

// Input type for condition value (driven by operator metadata from DB)
export type ConditionInputType = 'text' | 'number' | 'time' | 'array' | 'none'

// Single condition
export interface Condition {
  field: ConditionField
  operator: ConditionOperator
  values: string[]
}

// Conditions group with AND/OR logic
export interface ConditionsGroup {
  operator: 'AND' | 'OR'
  conditions: Condition[]
}

// Action types (dynamic from automation_action_types table)
export type ActionType = string

// Action for a rule (value type depends on the action type)
export interface RuleAction {
  type: ActionType
  value: unknown
}

// ============================================================================
// ACTION TYPE METADATA (from automation_action_types table)
// ============================================================================

export interface ActionValueSchemaField {
  name: string
  type: 'text' | 'switch' | 'number' | 'select' | 'color'
  label?: string
  placeholder?: string
  options?: Array<{ value: string; label: string }> | string[]
  source?: string
  min?: number
  max?: number
  step?: number
  /** When true, do not render the field label (e.g. when options are self-explanatory) */
  hideLabel?: boolean
  /** Explicit default value for this field */
  default?: string | number | boolean
}

export interface ActionValueSchema {
  type: 'select' | 'form'
  source?: string
  fields?: ActionValueSchemaField[]
  /** Schema-level default (for select types) */
  default?: unknown
}

/**
 * Derives the default value for an action type from its value_schema.
 * Enables DB-driven action types without hardcoding defaults per action type id.
 */
export function getDefaultValueFromSchema(schema: ActionValueSchema | undefined): unknown {
  if (!schema) return ''
  if (schema.default !== undefined && schema.default !== null) return schema.default
  if (schema.type === 'select' && schema.source === 'google_calendar_colors') {
    return '5' as const // Banana - common default for color picker
  }
  if (schema.type === 'form' && schema.fields?.length) {
    const obj: Record<string, unknown> = {}
    for (const f of schema.fields) {
      if (f.default !== undefined && f.default !== null) {
        obj[f.name] = f.default
      } else {
        switch (f.type) {
          case 'text':
            obj[f.name] = ''
            break
          case 'switch':
            obj[f.name] = false
            break
          case 'number':
            obj[f.name] = f.min ?? 0
            break
          case 'select':
            if (Array.isArray(f.options) && f.options.length) {
              const first = f.options[0]
              obj[f.name] = typeof first === 'string' ? first : first.value
            } else {
              obj[f.name] = ''
            }
            break
          case 'color':
            obj[f.name] = f.source === 'google_calendar_colors' ? '0' : ''
            break
          default:
            obj[f.name] = ''
        }
      }
    }
    return obj
  }
  return ''
}

export interface ActionTypeDBResponse {
  id: string
  display_name: string
  description: string | null
  exclusivity: 'exclusive' | 'additive'
  requires_intermediary_steps: boolean
  is_enabled: boolean
  display_order: number
  value_schema: ActionValueSchema
  created_at: string
  updated_at: string
}

export interface ActionTypeMetadata {
  id: string
  displayName: string
  description: string
  isEnabled: boolean
  valueSchema: ActionValueSchema
}

export function transformDBActionTypesToMetadata(
  dbActionTypes: ActionTypeDBResponse[]
): Record<string, ActionTypeMetadata> {
  const result: Record<string, ActionTypeMetadata> = {}

  for (const dbAction of dbActionTypes) {
    result[dbAction.id] = {
      id: dbAction.id,
      displayName: dbAction.display_name,
      description: dbAction.description || '',
      isEnabled: dbAction.is_enabled,
      valueSchema: dbAction.value_schema,
    }
  }

  return result
}

// Calendar resource from integrations.resources
export interface CalendarResource {
  id: string
  summary: string
  primary?: boolean
  backgroundColor?: string
  foregroundColor?: string
  accessRole?: string
}

// Integration calendar (from integrations_calendars table)
export interface IntegrationCalendar {
  id: string
  integration_id: string
  calendar_id: string
  automation_is_active: boolean
  channel_expiration?: string
  channel_is_active?: boolean
  channel_webhook_url?: string
  channel_resource_id?: string
  sync_window_start?: string
  sync_token?: string
  channel_renewal_failure_count?: number
  channel_renewal_failure_error?: string
  channel_renewal_last_attempt?: string
  updated_at: string
  created_at: string
}

// Automation rule
export interface AutomationRule {
  id: string
  user_id: string
  rule_name: string
  conditions: ConditionsGroup
  action: RuleAction
  calendar_id: string | null
  priority: number
  is_active: boolean
  is_ai_generated?: boolean
  integration_id_ref?: string | null
  calendar_id_external_ref?: string | null
  ai_notes?: string | null
  created_at: string
  updated_at: string
}

// Rule with calendar metadata (for list view)
export interface AutomationRuleWithAssignments extends AutomationRule {
  calendar: {
    id: string
    calendar_id: string
    integration_id?: string
    automation_is_active: boolean
    calendar_name?: string
    calendar_color?: string
  } | null
}

// Form data for creating/editing rules
export interface RuleFormData {
  rule_name: string
  conditions: ConditionsGroup
  action: RuleAction
  calendar_id: string | null
  priority: number
  is_active: boolean
}

// API request types
export interface CreateRuleRequest {
  rule_name: string
  conditions: ConditionsGroup
  action: RuleAction
  calendar_id: string
  priority?: number
  is_active?: boolean
}

export interface UpdateRuleRequest {
  rule_id: string
  rule_name?: string
  conditions?: ConditionsGroup
  action?: RuleAction
  calendar_id?: string
  priority?: number
  is_active?: boolean
}

export interface DeleteRuleRequest {
  rule_id: string
}

// API response types
export interface RuleResponse {
  success: boolean
  rule?: AutomationRule
  error?: string
}

export interface RulesListResponse {
  success: boolean
  rules?: AutomationRuleWithAssignments[]
  error?: string
}

export interface ActivateRulesResponse {
  success: boolean
  activated_calendars?: string[]
  error?: string
}

// Database response types
export interface ConditionFieldOperator {
  operator: string
  operator_label: string
  description: string
  input_type: ConditionInputType
}

export interface ConditionFieldDBResponse {
  id: string
  field_label: string
  field: string
  description: string | null
  value_type: 'text' | 'number' | 'array' | 'time'
  operators: ConditionFieldOperator[]
  created_at: string
  updated_at: string
}

// Field metadata for UI
export interface FieldMetadata {
  field: ConditionField
  label: string
  description: string
  operators: ConditionOperator[]
  operatorDetails: Record<ConditionOperator, {
    operator: ConditionOperator
    operator_label: string
    description: string
    input_type: ConditionInputType
  }>
}

// Operator metadata for UI
export interface OperatorMetadata {
  operator: ConditionOperator
  label: string
  description: string
  inputType: ConditionInputType
}

// Transformation functions
export function transformDBFieldsToMetadata(
  dbFields: ConditionFieldDBResponse[]
): Record<ConditionField, FieldMetadata> {
  const result: Partial<Record<ConditionField, FieldMetadata>> = {}

  for (const dbField of dbFields) {
    const operators = dbField.operators.map((op) => op.operator as ConditionOperator)
    const operatorDetails: Record<ConditionOperator, {
      operator: ConditionOperator
      operator_label: string
      description: string
      input_type: ConditionInputType
    }> = {} as any

    for (const op of dbField.operators) {
      operatorDetails[op.operator as ConditionOperator] = {
        operator: op.operator as ConditionOperator,
        operator_label: op.operator_label,
        description: op.description,
        input_type: op.input_type,
      }
    }

    result[dbField.field as ConditionField] = {
      field: dbField.field as ConditionField,
      label: dbField.field_label,
      description: dbField.description || '',
      operators,
      operatorDetails,
    }
  }

  return result as Record<ConditionField, FieldMetadata>
}

export function transformDBOperatorsToMetadata(
  dbFields: ConditionFieldDBResponse[]
): Record<ConditionOperator, OperatorMetadata> {
  const result: Partial<Record<ConditionOperator, OperatorMetadata>> = {}
  const seenOperators = new Set<string>()

  for (const dbField of dbFields) {
    for (const op of dbField.operators) {
      if (!seenOperators.has(op.operator)) {
        seenOperators.add(op.operator)
        result[op.operator as ConditionOperator] = {
          operator: op.operator as ConditionOperator,
          label: op.operator_label,
          description: op.description,
          inputType: op.input_type,
        }
      }
    }
  }

  return result as Record<ConditionOperator, OperatorMetadata>
}

// Preview types
export interface PreviewEventItem {
  event_id: string
  event_title: string | null
  event_start_time: string | null
  event_end_time: string | null
  event_is_recurring: boolean
  current_color: string | null
  target_color: string | null
  matching_rule_name: string | null
  skip_reason?: string
  applied_rule_priority?: number
}

export interface PreviewByRecurrence {
  regular: { to_update: number; skipped: number }
  recurring: { to_update: number; skipped: number }
}

export interface PreviewData {
  total_events: number
  events_to_update: number
  events_skipped: number
  by_recurrence: PreviewByRecurrence
  events_to_update_list: PreviewEventItem[]
  events_skipped_list: PreviewEventItem[]
  message?: string
}

export interface PreviewResponse {
  success: boolean
  preview?: PreviewData
  error?: string
}

